<template>
<section :id="wrapperId" class="map-wrapper"></section>
</template>

<script>
import './gltfAjax'

import mapAPILoader from './mapAPILoader'

import WGS84ToGCJ02 from './WGS84ToGCJ02'

import * as EventKeyMap from './EventKeys'

const JS_CODE = '&jscode=b59e1df130419e2f4f57aeda62156286'
const MAP_URL = `http://webapi.amap.com/maps?v=1.4.15&key=28458e7ac198f6cc3f6c5d5394c506ff${JS_CODE}&plugin=Map3D,AMap.GraspRoad,AMap.ControlBar,AMap.MouseTool,AMap.Geocoder,AMap.PlaceSearch,AMap.Heatmap`
const GLOBAL_MAP_API_VAR = 'AMap'

let idIndex = 0

const mapTypeMap = {
  AMap: {
    lngLatConverter: WGS84ToGCJ02
  }
}

const {createElement} = document
document.createElement = function (tagName) {
  let ele = createElement.apply(document, arguments)

  if (tagName === 'script') {
    // eslint-disable-next-line accessor-pairs
    Object.defineProperty(ele, 'src', {
      set (src) {
        delete this.src

        if (/^https?:\/\/restapi.amap.com\/v3\/geocode\/regeo/.test(src)) {
          src += JS_CODE
        }

        this.src = src
      },
      configurable: true
    })
  }

  return ele
}

export default {
  name: 'AMap',
  props: {
    satelliteMode: {
      type: Boolean
    },
    mapStyle: {
      type: String,
      default: 'normal'
    },
    features: {
      type: Array,
      default: () => ['bg', 'road']
    }
  },
  computed: {
    wrapperId: () => `map-wrapper-${idIndex++}`
  },
  methods: {
    /**
     * 获取当前地图中心点经纬度
     */
    getCenter () {
      return this.map.getCenter()
    },
    /**
     * 获取当前视野
     */
    getZoom () {
      return this.map.getZoom()
    },
    getBounds () {
      return this.map.getBounds()
    },
    getConverter () {
      return (this.isSatelliteMode ? mapTypeMap[this.mapType] : mapTypeMap.AMap).lngLatConverter
    },
    /**
     * 创建经过转换后的地图经纬度对象
     * @param lng 经度
     * @param lat 纬度
     */
    createLngLat (lng, lat) {
      let lngLat = this.getConverter()(lng, lat)
      lngLat = new this.MapAPI.LngLat(lngLat.lng, lngLat.lat)
      lngLat.oLng = lng
      lngLat.oLat = lat
      return lngLat
    },
    lngLat (lng, lat) {
      let lngLat = this.getConverter()(lng, lat)
      lngLat = new this.MapAPI.LngLat(lngLat.lng, lngLat.lat)
      lngLat.oLng = lng
      lngLat.oLat = lat
      return lngLat
    },
    lngLatToPixel (lnglat, zoom) {
      return this.map.lnglatToPixel(lnglat, zoom)
    },
    lngLatToContainer (lngLat) {
      return this.map.lngLatToContainer(lngLat)
    },
    drawCircle (opts) {
      let circle = new this.MapAPI.Circle(opts)
      circle.setMap(this.map)
      return circle
    },
    /**
     * 将经纬度转换为世界坐标
     * @param lngLat  经纬度对象
     */
    lngLatToGeodeticCoord (lngLat) {
      return this.map.lngLatToGeodeticCoord(lngLat)
    },
    /**
     * 根据鼠标坐标创建在地图上对应的像素点
     * @param offsetX 鼠标所在的x坐标
     * @param offsetY 鼠标所在的y坐标
     */
    createPixel (offsetX, offsetY) {
      return new this.MapAPI.Pixel(offsetX, offsetY)
    },
    /**
     * 移动当前地图到指定经纬度
     * @param lngLat  经纬度参数
     */
    panTo (lngLat) {
      this.map.panTo(lngLat)
    },
    setZoom (zoom) {
      this.map.setZoom(zoom | 0)
    },
    /**
     * 创建热力图
     */
    createHeatMap (opt) {
      return new this.MapAPI.Heatmap(this.map, opt)
    },
    /**
     * 创建3D图层
     * @param param 3D图层参数
     */
    createObject3DLayer (param = {}, converter) {
      param.zIndex = 100

      let object3DLayer = new this.MapAPI.Object3DLayer(param)
      object3DLayer.converter = converter
      this.object3DLayers.push(object3DLayer)
      this.map.add(object3DLayer)

      return object3DLayer
    },
    /**
     * 移除一个3D图层
     * @param object3DLayer 3D图层
     */
    removeObject3DLayer (object3DLayer) {
      let {object3DLayers} = this

      if (!Array.isArray(object3DLayer)) {
        return
      }

      let {map} = this
      let index = object3DLayers.indexOf(object3DLayer)

      if (index > -1) {
        object3DLayers.splice(index, 1)
        map.remove(object3DLayer)
      }
    },
    /**
     * 创建3D网格
     * @param extData 用户数据
     */
    createMesh (extData) {
      let mesh = new this.MapAPI.Object3D.Mesh()
      mesh.extData = extData
      return mesh
    },
    /**
     * 创建一个GLTF模型对象
     */
    getGltfObj () {
      return new this.MapAPI.GltfLoader()
    },
    createOverlayGroup (eles = [], converter) {
      let overlayGroup = new this.MapAPI.OverlayGroup(eles)
      overlayGroup.converter = converter
      this.object3DLayers.push(overlayGroup)
      this.map.add(overlayGroup)
      return overlayGroup
    },
    createMarker (param) {
      let marker = new this.MapAPI.Marker(param)
      marker.setMap(this.map)
      return marker
    },
    createIcon (param) {
      return new this.MapAPI.Icon(param)
    },
    createSize (width, height) {
      return new this.MapAPI.Size(width, height)
    },
    createPolyline (opts) {
      opts = opts || {}
      opts.map = this.map
      return new this.MapAPI.Polyline(opts)
    },
    removeOverlay (overlay) {
      this.map.remove(overlay)
    },
    addMapEle (mapEle) {
      this.map.add(mapEle)
    },
    removeMapEle (mapEle) {
      this.map.remove(mapEle)
    },
    createText (params) {
      let text = new this.MapAPI.Text(params)
      text.setMap(this.map)
      return text
    },
    /**
     * 根据像素, 获取像素所对应的3D对象
     * @param px 像素点
     * @param 3D图层列表
     * @param 未知
     */
    getObject3DByContainerPos (px, containList, flag) {
      return this.map.getObject3DByContainerPos(px, containList, flag)
    },
    /**
     * 插件调用
     * @param 插件的参数, Array类型
     * @param 插件加载成功后的回调函数
     */
    plugin (params, callback) {
      this.map.plugin(params, callback)
    },
    /**
     * 改变地图上数据图形位置
     */
    changeOverLayerPosition () {
      let {object3DLayers} = this

      if (!Array.isArray(object3DLayers)) {
        return
      }

      object3DLayers.forEach(object3DLayer => {
        let positionConverter = object3DLayer.converter

        if (typeof positionConverter === 'function') {
          positionConverter(object3DLayer)
        }
      })
    },
    /**
     * 切换地图图层具体实现
     * @param mapType AMap、BMap
     */
    setMapTiles (mapType) {
      if (mapType === this.mapType) {
        return
      }

      this.mapType = mapType
    },
    /**
     * 地图图层切换事件
     * @param mapType AMap、BMap
     */
    onMapTilesChanged (mapType) {
      this.mapIsCreated(this.setMapTiles, () => {
        this.tmpMapType = mapType
      }, mapType)
    },
    /**
     * 切换卫星地图具体实现
     * @param isSatelliteMode  true: 则显示卫星地图; false: 显示电子地图
     */
    setSatelliteMode (isSatelliteMode) {
      if (isSatelliteMode === this.isSatelliteMode) {
        return
      }

      this.isSatelliteMode = isSatelliteMode

      this.map[isSatelliteMode ? 'add' : 'remove'](this.satelliteLayer)
    },
    /**
     * 切换卫星地图事件
     * @param isSatelliteMode  true: 则显示卫星地图; false: 显示电子地图
     */
    onSatelliteModeChange (isSatelliteMode) {
      this.mapIsCreated(this.setSatelliteMode, () => {
        this.tmpIsSatelliteMode = isSatelliteMode
      }, isSatelliteMode)
    },
    setMapStyle (mapStyle) {
      this.map.setMapStyle(`amap://styles/${mapStyle}`)
    },
    onMapStyleChanged (mapStyle) {
      this.mapStyle = mapStyle

      this.mapIsCreated(this.setMapStyle, () => {
        this.tmpMapStyle = mapStyle
      }, mapStyle)
    },
    /**
     * 根据点设置中心点
     * @param points  点的集合
     */
    setMapCenterByPoints (points) {
      points = points.filter(({position}) => position.lng !== -99999 && position.lat !== -99999)
      let lngs = points.map(point => point.position.lng)
      let lats = points.map(point => point.position.lat)
      let minLng = Math.min(...lngs)
      let maxLng = Math.max(...lngs)
      let minLat = Math.min(...lats)
      let maxLat = Math.max(...lats)
      let centerLng = (maxLng + minLng) / 2
      let centerLat = (maxLat + minLat) / 2
      let centerPoint = this.createLngLat(centerLng, centerLat)
      this.map.setCenter(centerPoint)
    },
    createInfoWindow (param) {
      return new this.MapAPI.InfoWindow(param)
    },
    /**
     * 根据点设置中心点
     * @param points  点的集合
     */
    onSetMapCenterByPoints (points) {
      this.setMapCenterByPoints(points)
    },
    onBestViewChanged (bestView) {
      this.bestView = bestView
    },
    /**
     * 设置最佳视角
     * @param bestView  最佳视角参数
     */
    setBestView () {
      let {map, bestView = {}} = this
      map.setPitch(bestView.pitch | 0)
      map.setRotation(bestView.heading | 0)
      map.setZoom(bestView.range | 0)
    },
    /**
     * 设置最佳视角
     * @param bestView  最佳视角参数
     */
    onSetBestView () {
      this.setBestView()
    },
    setFitView (overLayers) {
      this.map.setFitView(overLayers)
    },
    handleSetFitView (overlays) {
      this.map.setFitView(overlays)
    },
    handleRemoveOverlay (overlays) {
      this.map.remove(overlays)
    },
    handleSetCenter (lngLat) {
      this.map.setCenter(lngLat)
    },
    handleSetZoom (zoom) {
      this.map.setZoom(zoom)
    },
    handleClearMap () {
      this.map.clearMap()
    },
    /**
     * 获取全局MapAPI对象
     */
    getMapAPI () {
      return this.MapAPI || null
    },
    /**
     * 获取全局MapAPI对象
     * @param callback  回调函数, 用于传递全局MapAPI对象
     */
    onGetMapAPI (callback) {
      let MapAPI = this.getMapAPI()

      if (MapAPI !== null) {
        callback(MapAPI)
      }
    },
    /**
     * 获取当前地图对象
     */
    getMap () {
      return this.map ? this : null
    },
    /**
     * 获取当前地图对象
     * @param callback  回调函数, 用于传递当前地图对象
     */
    onGetMap (callback) {
      let map = this.getMap()

      if (map !== null) {
        callback(map)
      }
    },
    /**
     * 创建地图
     * @param MapAPI  全局MapAPI对象
     */
    createMap (MapAPI) {
      return new Promise(resolve => {
        let map = this.map = new MapAPI.Map(this.wrapperId, {
          // viewMode: '3D',
          showBuildingBlock: false,
          expandZoomRange: true,
          zooms: [3, 18],
          pitch: 45,
          zoom: 16,
          doubleClickZoom: false,
          mapStyle: `amap://styles/${this.mapStyle}`,
          features: this.features
        })

        this.satelliteLayer = new MapAPI.TileLayer.Satellite()

        if (this.satelliteMode) {
          map.add(this.satelliteLayer)
        }

        map.on('complete', resolve)
      })
    },
    onZoom (event) {
      this.$eventBus.emit(EventKeyMap.MAP_ZOOM, event)
    },
    /**
     * 地图视野发生变化的事件
     */
    onZoomEnd (event) {
      this.$eventBus.emit(EventKeyMap.MAP_ZOOM_END, event)
    },
    onMoveStart (event) {
      this.$eventBus.emit(EventKeyMap.MAP_MOVE_START, event)
    },
    onMapMove (event) {
      this.$eventBus.emit(EventKeyMap.MAP_MOVE, event)
    },
    onMoveEnd (event) {
      this.$eventBus.emit(EventKeyMap.MAP_MOVE_END, event)
    },
    /**
     * 当鼠标在地图上点击时触发的事件
     * @param event 地图事件对象
     */
    onClick (event) {
      this.$eventBus.emit(EventKeyMap.MAP_CLICK, event)
    },
    /**
     * 当鼠标在地图上右击时触发的事件
     * @param event 地图事件对象
     */
    onMouseRightClick (event) {
      this.$eventBus.emit(EventKeyMap.MAP_MOUSE_RIGHT_CLICK, event)
    },
    /**
     * 当鼠标在地图上移动时触发的事件
     * @param event 事件消息对象
     */
    onMouseMove (event) {
      this.$eventBus.emit(EventKeyMap.MAP_MOUSE_MOVE, event)
    },
    onMapRemoveOverlay (overlays) {
      this.map.remove(overlays)
    },
    /**
     * 添加地图监听事件
     */
    bindMapEventListener () {
      let {map, $eventBus} = this
      map.on('zoom', this.onZoom)
      map.on('zoomend', this.onZoomEnd)
      map.on('movestart', this.onMoveStart)
      map.on('mapmove', this.onMapMove)
      map.on('moveend', this.onMoveEnd)
      map.on('click', this.onClick)
      map.on('rightclick', this.onMouseRightClick)
      map.on('mousemove', this.onMouseMove)
      $eventBus.on(EventKeyMap.MAP_REMOVE_OVERLAY, this.onMapRemoveOverlay)
      $eventBus.on(EventKeyMap.MAP_CENTER_CHANGE, this.handleSetCenter)
      $eventBus.on(EventKeyMap.MAP_ZOOM_CHANGE, this.handleSetZoom)
      $eventBus.on(EventKeyMap.MAP_FIT_VIEW_CHANGE, this.handleSetFitView)
      $eventBus.on(EventKeyMap.MAP_CLEAR, this.handleClearMap)
    },
    /**
     * 移除地图监听事件
     */
    unbindMapEventListener () {
      let {map} = this

      if (!map) {
        return
      }

      let {$eventBus} = this

      map.off('zoom', this.onZoom)
      map.off('zoomend', this.onZoomEnd)
      map.off('movestart', this.onMoveStart)
      map.off('mapmove', this.onMapMove)
      map.off('moveend', this.onMoveEnd)
      map.off('click', this.onClick)
      map.off('rightclick', this.onMouseRightClick)
      map.off('mousemove', this.onMouseMove)
      $eventBus.off(EventKeyMap.MAP_REMOVE_OVERLAY, this.onMapRemoveOverlay)
      $eventBus.off(EventKeyMap.MAP_CENTER_CHANGE, this.handleSetCenter)
      $eventBus.off(EventKeyMap.MAP_ZOOM_CHANGE, this.handleSetZoom)
      $eventBus.off(EventKeyMap.MAP_FIT_VIEW_CHANGE, this.handleSetFitView)
      $eventBus.off(EventKeyMap.MAP_CLEAR, this.handleClearMap)
    },
    /**
     * 根据经纬度获取位置
     * @param lng       经度
     * @param lat       纬度
     */
    getGeoInfo (lng, lat) {
      return new Promise((resolve, reject) => {
        let point = this.createLngLat(lng, lat)

        this.geocoder.getAddress(point, (status, result) => {
          if (status === 'complete') {
            this.map.setCenter(point)
            resolve({
              lngLat: point,
              geo: result
            })
          } else {
            reject(new Error('当前经纬度查询不到地址'))
          }
        })
      })
    },
    /**
     * 当地图API加载完成的事件
     * @param MapAPI  全局MapAPI对象
     */
    onMapAPIReady (MapAPI) {
      this.MapAPI = MapAPI
      this.$eventBus.emit(EventKeyMap.MAP_API_LOADED, MapAPI)
      this.geocoder = new MapAPI.Geocoder()

      this.createMap(MapAPI).then(() => {
        this.bindMapEventListener()

        let {tmpMapType, tmpIsSatelliteMode, tmpMapStyle} = this

        if (tmpMapType) {
          this.setMapTiles(tmpMapType)
        }

        if (tmpIsSatelliteMode) {
          this.setSatelliteMode(tmpIsSatelliteMode)
        }

        if (tmpMapStyle) {
          this.setMapStyle(tmpMapStyle)
        }

        let map = this.getMap()
        this.$eventBus.emit(EventKeyMap.MAP_CREATED, map)
      })
    },
    /**
     * 加载地图API
     */
    loadMap () {
      mapAPILoader(MAP_URL, GLOBAL_MAP_API_VAR).then(this.onMapAPIReady)
    },
    /**
     * 判断地图是否加载完成, 如果加载完成, 则调用success函数, 失败则调用failed函数
     * @param success 判断地图加载完成时调用的函数
     * @param failed  判断地图加载失败时调用的函数
     * @param args    额外参数, 传递给success函数
     */
    mapIsCreated (success, failed, ...args) {
      let map = this.getMap()

      if (map === null) {
        failed()
      } else {
        success(...args)
      }
    }
  },
  created () {
    this.loadMap()
    this.mapType = 'AMap'
    this.object3DLayers = []
  },
  beforeDestroy () {
    this.$eventBus.emit(EventKeyMap.MAP_DESTROY)
    this.unbindMapEventListener()

    this.object3DLayers = null

    let map = this.map

    if (map) {
      map.destroy()
      this.map = null
    }
  }
}
</script>

<style lang="scss" scoped>
.map-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  /deep/ {
    .amap-copyright,
    .amap-logo {
      display: none !important;
    }

    .amap-layer[class="amap-layer"] {
      > img {
        display: none !important;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Map/index.vue